import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ExternalLink, Sparkles, X } from 'lucide-react';
import { useHygieneAction, useHygieneFinding } from '../../../hooks/hygiene/useHygiene.ts';
import type { HygieneAction, HygieneCheckMeta, HygieneFinding, HygieneMember } from '../../../request/hygiene/hygieneRequests.ts';
import { CHECK_COPY, EVENT_LABELS, SEVERITY_LABELS, SEVERITY_PILL, STATE_LABELS, findingLink, formatDate, reasonLabel } from '../labels.ts';
import { AcceptDialog, AssignDialog, NoteDialog } from './HygieneDialogs.tsx';
import { useFocusTrap } from './useFocusTrap.ts';

type Dialog = null | 'assign' | 'accept' | 'reject' | 'reopen' | 'dismiss';

const btn = 'rounded-lg px-3 py-2 text-sm font-medium shadow-sm disabled:cursor-not-allowed disabled:opacity-50';
const primary = `${btn} bg-blue-700 text-white hover:bg-blue-800`;
const secondary = `${btn} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`;

export function FindingPanel({
  findingId,
  auditYearId,
  checks,
  members,
  canManage,
  canApprove,
  currentUserId,
  onClose,
}: {
  findingId: string;
  auditYearId: string;
  checks: Map<string, HygieneCheckMeta>;
  members: Map<number, HygieneMember>;
  canManage: boolean;
  canApprove: boolean;
  currentUserId?: number;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useHygieneFinding(findingId);
  const action = useHygieneAction();
  const [dialog, setDialog] = useState<Dialog>(null);
  const panelRef = useRef<HTMLElement>(null);
  // Trap focus in the panel while no dialog is open (an open dialog traps its own).
  useFocusTrap(panelRef, dialog === null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dialog) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onClose]);

  const f = data?.finding;
  const run = (a: HygieneAction, body: { owner_user_id?: number; reason_code?: string; note?: string } = {}) =>
    action.mutate({ id: findingId, action: a, body: { audit_year_id: auditYearId, ...body } }, { onSuccess: () => setDialog(null) });

  const name = (id?: number) => (id ? members.get(id)?.name ?? `User ${id}` : '');

  return createPortal(
    <div className="fixed inset-0 z-[200] flex justify-end" role="dialog" aria-modal="true" aria-label="Hygiene item">
      <div className="absolute inset-0 bg-black/30" aria-hidden onClick={onClose} />
      <aside ref={panelRef} className="relative z-10 flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            {f ? (
              <div className="flex flex-wrap items-center gap-2">
                {f.state === 'suggested' ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 ring-1 ring-blue-200">
                    <Sparkles className="h-3 w-3" aria-hidden /> AI review{f.ai_verdict === 'unsure' ? ' · unsure' : ''}
                  </span>
                ) : (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${SEVERITY_PILL[f.severity]}`}>{SEVERITY_LABELS[f.severity]}</span>
                )}
                <span className="text-xs text-gray-500">{STATE_LABELS[f.state]}</span>
              </div>
            ) : null}
            <h2 className="mt-1 text-lg font-semibold text-gray-900">{f ? checks.get(f.check)?.title ?? f.check : 'Hygiene item'}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? <p className="px-5 py-6 text-sm text-gray-500">Loading…</p> : null}
        {isError ? <p className="px-5 py-6 text-sm text-red-700">This item could not be loaded.</p> : null}

        {f ? (
          <div className="flex-1 space-y-5 px-5 py-4">
            <FindingBody f={f} owner={f.owner_user_id ? name(f.owner_user_id) : f.owner_label} approximate={Boolean(checks.get(f.check)?.approximate)} />

            {f.accept_reason && (f.state === 'accepted' || f.state === 'pending_approval') ? (
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span className="font-medium">{reasonLabel(f.accept_reason)}</span>
                {f.accept_note ? <> — {f.accept_note}</> : null}
                {f.requested_by ? <span className="block text-xs text-gray-500">Requested by {name(f.requested_by)}</span> : null}
              </div>
            ) : null}

            {canManage || canApprove ? (
              <div className="flex flex-wrap gap-2">
                {f.state === 'suggested' && canManage ? (
                  <>
                    <button type="button" className={primary} disabled={action.isPending} onClick={() => run('confirm')}>Confirm</button>
                    <button type="button" className={secondary} disabled={action.isPending} onClick={() => setDialog('dismiss')}>Dismiss</button>
                  </>
                ) : null}
                {(f.state === 'open' || f.state === 'assigned') && canManage ? (
                  <>
                    <button type="button" className={secondary} onClick={() => setDialog('assign')}>Assign</button>
                    <button type="button" className={secondary} onClick={() => setDialog('accept')}>Accept</button>
                  </>
                ) : null}
                {f.state === 'pending_approval' && canApprove && f.requested_by !== currentUserId ? (
                  <>
                    <button type="button" className={primary} disabled={action.isPending} onClick={() => run('approve')}>Approve</button>
                    <button type="button" className={secondary} onClick={() => setDialog('reject')}>Reject</button>
                  </>
                ) : null}
                {f.state === 'pending_approval' && canApprove && f.requested_by === currentUserId ? (
                  <p className="text-sm text-gray-500">You requested this; another org admin must approve it.</p>
                ) : null}
                {(f.state === 'accepted' || f.state === 'pending_approval') && canManage ? (
                  <button type="button" className={secondary} onClick={() => setDialog('reopen')}>Reopen</button>
                ) : null}
              </div>
            ) : null}

            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">History</h3>
              <ol className="space-y-2 border-l border-gray-200 pl-4">
                {(data?.events ?? []).map((e) => (
                  <li key={e.id} className="text-sm">
                    <span className="font-medium text-gray-800">{EVENT_LABELS[e.event_type] ?? e.event_type}</span>
                    <span className="text-gray-500">
                      {' '}· {formatDate(e.created_at)}
                      {e.actor_user_id ? ` · ${name(e.actor_user_id)}` : ' · system'}
                    </span>
                    {e.reason_code ? <span className="block text-gray-600">{reasonLabel(e.reason_code)}</span> : null}
                    {e.note ? <span className="block text-gray-600">{e.note}</span> : null}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        ) : null}
      </aside>

      {f && dialog === 'assign' ? (
        <AssignDialog finding={f} members={Array.from(members.values())} busy={action.isPending} onClose={() => setDialog(null)} onSubmit={(owner, note) => run('assign', { owner_user_id: owner, note })} />
      ) : null}
      {f && dialog === 'accept' ? (
        <AcceptDialog finding={f} busy={action.isPending} onClose={() => setDialog(null)} onSubmit={(reason, note) => run('accept', { reason_code: reason, note })} />
      ) : null}
      {f && dialog === 'reject' ? (
        <NoteDialog title="Reject acceptance" subtitle={f.record_label} confirmLabel="Reject" noteRequired busy={action.isPending} onClose={() => setDialog(null)} onSubmit={(note) => run('reject', { note })} />
      ) : null}
      {f && dialog === 'reopen' ? (
        <NoteDialog title="Reopen item" subtitle={f.record_label} confirmLabel="Reopen" noteRequired={false} busy={action.isPending} onClose={() => setDialog(null)} onSubmit={(note) => run('reopen', { note })} />
      ) : null}
      {f && dialog === 'dismiss' ? (
        <NoteDialog title="Dismiss suggestion" subtitle={f.record_label} confirmLabel="Dismiss" noteRequired={false} busy={action.isPending} onClose={() => setDialog(null)} onSubmit={(note) => run('dismiss', { note })} />
      ) : null}
    </div>,
    document.body
  );
}

function FindingBody({ f, owner, approximate }: { f: HygieneFinding; owner?: string; approximate: boolean }) {
  const link = findingLink(f);
  const copy = CHECK_COPY[f.check];
  return (
    <>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Record</p>
        <p className="text-sm text-gray-900">{f.record_label}</p>
        {link ? (
          <Link to={link.to} state={link.state} onClick={() => window.scrollTo(0, 0)} className="mt-1 inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
            {link.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : null}
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{f.source === 'ai' ? 'What the AI review found' : 'What was found'}</p>
        <p className="text-sm text-gray-900">{f.detail}</p>
        {f.acceptance_expired ? <p className="mt-1 text-sm text-amber-800">It was accepted before, but the record changed, so the acceptance expired.</p> : null}
        {approximate ? <p className="mt-1 text-xs text-gray-500">Approximate check.</p> : null}
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Owner</p>
        <p className="text-sm text-gray-900">{owner || 'Unassigned'}</p>
      </div>
      {copy ? (
        <>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Why it matters</p>
            <p className="text-sm text-gray-700">{copy.why}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">How to fix</p>
            <p className="text-sm text-gray-700">{copy.fix}</p>
          </div>
        </>
      ) : null}
      <p className="text-xs text-gray-500">
        Found {formatDate(f.first_seen_at)} · last checked {formatDate(f.last_seen_at)}
        {f.resolved_at ? ` · fixed ${formatDate(f.resolved_at)}` : ''}
      </p>
    </>
  );
}
